import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { PaymentMethodVM, PaymentService } from "@propertelligent/client-api";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import TextButton from "../../../newComponents/buttons/text-button";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import apiHelper from "../../../utils/apiHelper";
import { GetImages } from "../../../utils/GetImages";
import { ACCOUNT_NAME_MAX_LENGTH } from "../../../components/firstTimeUserExperience/Link";

export interface EditPlaidAccountNameModalProps {
  isOpen: boolean;
  paymentMethod: PaymentMethodVM | null;
  onClose: () => void;
  onRenamed: () => void;
}

/**
 * Shared "rename a connected Plaid account" side drawer, reused across every portal (Organization,
 * Platform Portal, My Settings/OrganizationUser, Tenant, Contractor) that lists Plaid payment accounts.
 * This is deliberately a rename-only PUT (no plaidPaymentMethod.publicAccessToken) so it never requires
 * -- or disturbs -- the account's existing Plaid connection; see PaymentMethodService.UpdateAsync's
 * Plaid case, which skips re-exchanging/re-hydrating when no fresh public token is supplied.
 */
const EditPlaidAccountNameModal = ({ isOpen, paymentMethod, onClose, onRenamed }: EditPlaidAccountNameModalProps) => {
  const [accountName, setAccountName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && paymentMethod) {
      setAccountName(paymentMethod.accountName || "");
    }
  }, [isOpen, paymentMethod]);

  const trimmedName = accountName.trim();
  const originalName = (paymentMethod?.accountName || "").trim();
  // Bug #85: Save must stay disabled until the name actually changes (Edit open alone ≠ dirty).
  const isDirty = trimmedName !== originalName;
  const canSave = Boolean(trimmedName) && isDirty && !isSaving;

  const handleSave = async () => {
    if (!paymentMethod || !canSave) return;

    setIsSaving(true);
    try {
      const res = await apiHelper(
        PaymentService.putApiPaymentMethod,
        {
          title: "Payment Account Renamed",
          successMessage: "Account name updated successfully",
          failureMessage: "Unable to update account name",
          showNotification: true,
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
        },
        {
          ...paymentMethod,
          accountName: trimmedName.slice(0, ACCOUNT_NAME_MAX_LENGTH),
          plaidPaymentMethod: {},
        }
      );

      if (res?.data?.success) {
        onRenamed();
      }
    } catch (error) {
      console.error("Error renaming payment account:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <RightSideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Payment Account"
      maxWidth={450}
      buttonlabel={undefined}
      formId={undefined}
      customFooter={
        <div className="d-flex justify-content-end gap-2">
          <TextButton label="Cancel" onClick={onClose} />
          <SimpleButton
            label="Save"
            onClick={handleSave}
            isDisabled={!canSave}
            isLoading={isSaving}
          />
        </div>
      }
    >
      <Card className="no-bg">
        <Form.Group>
          <Form.Label>Account Name</Form.Label>
          <Form.Control
            type="text"
            value={accountName}
            maxLength={ACCOUNT_NAME_MAX_LENGTH}
            placeholder="Account name"
            autoFocus
            disabled={isSaving}
            onChange={(e) => setAccountName(e.target.value)}
          />
        </Form.Group>
      </Card>
    </RightSideModal>
  );
};

export default EditPlaidAccountNameModal;
